import React from 'react';
import moment from 'moment';
import { DayOfWeekSelections, DayOfWeek } from 'parent-portal/forms/JobRequestTypes';
import { FormFieldProps } from '../../FormTypes';
import { Grid } from '@mui/material';
import FormField from '../../FormField';
import ErrorText from '../../ErrorText';
import { useWidth } from 'reusableComponents/useWidth';
import DayButton from './DayButton';
import { useFieldValidation } from '../../FormHook';
import { checkIfAllDaysHaveTime, checkIfDurationIsValid, checkIfEndTimeIsValid } from './JobTimeValidation';
import { TimePicker } from 'library';
import SlotsPicker from './SlotsPicker';

interface JobSameTimeProps {
    daysOfTheWeek: FormFieldProps<DayOfWeekSelections>;
}

export default function JobMultiTimePicker({ daysOfTheWeek }: JobSameTimeProps) {
    const { value, setValue } = daysOfTheWeek;

    const width = useWidth();

    useFieldValidation(daysOfTheWeek, checkIfAllDaysHaveTime(value));

    function setTime(day: string, time: moment.Moment | null, type: string) {
        const updatedDay = {
            ...value[day as keyof typeof value],
        };

        if (type === 'start') {
            updatedDay.start = time;
        } else {
            updatedDay.end = time;
        }

        setValue({
            ...value,
            [day as keyof typeof value]: updatedDay,
        });
    }

    const timePicker = ({ day, dayName }: { day: DayOfWeek; dayName: string }) => {
        const startIsMidnight = day.start?.hour() === 0 && day.start.minute() === 0;
        const endIsMidnight = day.end?.hour() === 0 && day.end.minute() === 0;
        const startIsNoon = day.start?.hour() === 12 && day.start.minute() === 0;
        const endIsNoon = day.end?.hour() === 12 && day.end.minute() === 0;
        return (
            <Grid container alignItems="center" gap={1}>
                <Grid item xs={12} sm={4} style={{ width: width === 'xs' ? '100%' : undefined }}>
                    <FormField style={{ margin: ['md', 'lg', 'xl'].includes(width) ? 0 : undefined }}>
                        <TimePicker
                            format={`hh:mm ${startIsMidnight ? '\\mi\\dnig\\ht' : startIsNoon ? 'noon' : 'a'}`}
                            value={day.start}
                            onChange={(time) => setTime(dayName, time, 'start')}
                            sx={{ width: '100%' }}
                        />
                    </FormField>
                </Grid>
                <Grid item xs={12} sm={4} style={{ width: width === 'xs' ? '100%' : undefined }}>
                    <FormField
                        style={{
                            margin: ['md', 'lg', 'xl'].includes(width) ? 0 : undefined,
                        }}
                    >
                        <TimePicker
                            format={`hh:mm ${endIsMidnight ? '\\mi\\dnig\\ht' : endIsNoon ? 'noon' : 'a'}`}
                            value={day.end}
                            onChange={(time) => setTime(dayName, time, 'end')}
                            sx={{ width: '100%' }}
                        />
                        {!checkIfEndTimeIsValid(day) && <ErrorText>Invalid end time</ErrorText>}

                        <Grid container xs={12}>
                            {!checkIfDurationIsValid(day) && (
                                <ErrorText>Duration must be at least 30 minutes</ErrorText>
                            )}
                        </Grid>
                    </FormField>
                </Grid>
                <Grid item xs={12} sm={2} style={{ marginBottom: 15 }}>
                    <SlotsPicker
                        key={dayName}
                        slotsAvailable={day.slots}
                        setSlotsAvailable={(slots) =>
                            daysOfTheWeek.setValue({
                                ...daysOfTheWeek.value,
                                [dayName]: { ...day, slots },
                            })
                        }
                        label="Workers"
                    />
                </Grid>
            </Grid>
        );
    };

    return (
        <Grid container xs={12} direction="column">
            {Object.entries(value).map(([dayName, day]) => {
                return day.selected ? (
                    <Grid container style={{ marginBottom: 30, flexWrap: 'nowrap' }} gap={2}>
                        <Grid item style={{ alignSelf: 'center' }}>
                            <DayButton dayName={dayName} />
                        </Grid>
                        {timePicker({ day: day, dayName: dayName })}
                    </Grid>
                ) : null;
            })}
        </Grid>
    );
}
